import { Component, DestroyRef, Inject, inject, Input, OnInit, ViewChild } from '@angular/core';
import { BillingInvoice, BillingInvoiceStatus, BillingResolve, CoreData, Money } from '@shared/models';
import { ActivatedRoute } from '@angular/router';
import { showSpinner, sortArray, TypedRoute } from '@shared/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { breakPoints } from '@core/utils';
import { ArivoPaginatorComponent } from '@shared/components/ui-elements';
import { AmplitudeEventService, CoreDataService, ProviderSettingsService } from '@core/services';
import { InvoiceDownloaded } from '@integrations/amplitude';
import { ToastService } from '@shared/services';
import { TranslateService } from '@ngx-translate/core';
import { BillingApiService } from '../billing-api.service';

interface InvoicePeriod {
  status: BillingInvoiceStatus | null;
  costs: Money[];
  invoices: BillingInvoice[];
  expanded: boolean;
}

type BillingInvoiceSource = 'current' | 'archived';

@Component({
  selector: 'app-billing-invoices',
  templateUrl: './billing-invoices.component.html',
  styleUrl: './billing-invoices.component.scss',
})
export class BillingInvoicesComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  @Input() invoiceSource: BillingInvoiceSource = 'current';

  startOpen: boolean = false;

  invoicePeriods: string[] = [];
  invoices: { [key: string]: InvoicePeriod } = {};

  orderedBy: 'date' = 'date';
  orderedByOrder: 'asc' | 'desc' = 'desc';
  clearedStatus = ['cleared', 'refunded', 'deducted'];
  defaultCurrency: Money['currency'] = 'EUR';
  hasExternalPaymentMethod: boolean = false;
  @ViewChild('paginator') paginator: ArivoPaginatorComponent | undefined;

  constructor(
    @Inject(ActivatedRoute) private route: TypedRoute<BillingResolve>,
    private amplitudeEventService: AmplitudeEventService,
    private providerSettingsService: ProviderSettingsService,
    private billingApiService: BillingApiService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private coreDataService: CoreDataService,
  ) {}

  ngOnInit() {
    this.startOpen = window.innerWidth > breakPoints.md;
    this.defaultCurrency = this.providerSettingsService.getProviderGeneralData().default_currency || 'EUR';
    this.coreDataService.coreData.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((coreData: CoreData) => {
      this.hasExternalPaymentMethod = coreData.customer_settings?.has_external_payment_method;
    });
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.invoicePeriods = [];
      this.invoices = {};

      const invoices = sortArray(this.getInvoices(data), (x) => x.invoice_date, 'desc');

      let startDate: Date | undefined;
      let endDate: Date | undefined;

      // Group invoices by period
      invoices.forEach((invoice) => {
        const invoiceDateFirstOfMonth = new Date(invoice.invoice_date.getFullYear(), invoice.invoice_date.getMonth(), 1);
        const key = this.getFormattedPeriod(invoiceDateFirstOfMonth);

        if (!startDate || invoiceDateFirstOfMonth < startDate) {
          startDate = invoiceDateFirstOfMonth;
        }
        if (!endDate || invoiceDateFirstOfMonth > endDate) {
          endDate = invoiceDateFirstOfMonth;
        }

        if (key in this.invoices) {
          if (
            !this.isArchivedInvoiceSource &&
            this.invoices[key].status !== 'open' &&
            !this.clearedStatus.includes(invoice.status)
          ) {
            this.invoices[key].status = 'open';
          }
          if (invoice.status !== 'nonrefundable') {
            this.addCosts(this.invoices[key].costs, invoice.costs);
          }
          this.invoices[key].invoices.push(invoice);
        } else {
          this.invoices[key] = {
            status: this.getPeriodStatus(invoice),
            costs: this.getPeriodCosts(invoice),
            invoices: [invoice],
            expanded: false,
          };
        }
      });

      // Create a list of months between the earliest and the latest invoice period
      if (startDate && endDate) {
        for (let date = new Date(endDate.getTime()); date >= startDate; date.setMonth(date.getMonth() - 1)) {
          const key = this.getFormattedPeriod(date);
          if (!(key in this.invoices)) {
            this.invoices[key] = {
              status: null,
              costs: this.getEmptyPeriodCosts(),
              invoices: [],
              expanded: false,
            };
          }
          this.invoicePeriods.push(key);
        }
      }
    });
  }

  get isArchivedInvoiceSource() {
    return this.invoiceSource === 'archived';
  }

  // customers with an external payment method pay outside the portal, so the invoice status would be misleading
  get showInvoiceStatus() {
    return !this.isArchivedInvoiceSource && !this.hasExternalPaymentMethod;
  }

  private getInvoices(data: BillingResolve): BillingInvoice[] {
    if (this.isArchivedInvoiceSource) {
      return data.billingApiResponse.archived_invoices;
    }

    return data.billingApiResponse.invoices;
  }

  private getPeriodStatus(invoice: BillingInvoice): BillingInvoiceStatus {
    return this.clearedStatus.includes(invoice.status) ? 'cleared' : 'open';
  }

  private getPeriodCosts(invoice: BillingInvoice): Money[] {
    if (invoice.status === 'nonrefundable') {
      return this.getZeroCosts(invoice.costs);
    }

    return invoice.costs.map((cost) => ({ ...cost }));
  }

  private getZeroCosts(costs: Money[]): Money[] {
    return costs.map((cost) => ({
      amount: 0,
      currency: cost.currency,
    }));
  }

  private getEmptyPeriodCosts(): Money[] {
    return [
      {
        amount: 0,
        currency: this.defaultCurrency,
      },
    ];
  }

  private addCosts(targetCosts: Money[], costsToAdd: Money[]) {
    costsToAdd.forEach((costToAdd) => {
      const existingCost = targetCosts.find((cost) => cost.currency === costToAdd.currency);

      if (existingCost) {
        existingCost.amount += costToAdd.amount;
      } else {
        targetCosts.push({ ...costToAdd });
      }
    });
  }

  get paginatedPeriods() {
    if (this.paginator) {
      return this.invoicePeriods.slice(this.paginator.startIndex, this.paginator.endIndex);
    } else {
      return this.invoicePeriods.slice(0, 10);
    }
  }

  orderByDate() {
    this.orderedByOrder = this.orderedBy === 'date' && this.orderedByOrder === 'asc' ? 'desc' : 'asc';
    this.orderedBy = 'date';

    sortArray(this.invoicePeriods, (x) => x, this.orderedByOrder);
  }

  collapse(start: number, end: number) {
    for (let i = start; i < end; i++) {
      this.invoices[this.invoicePeriods[i]].expanded = false;
    }
  }

  getFormattedPeriod(date: Date) {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const formattedMonth = month >= 10 ? month : `0${month}`;
    return `${year}-${formattedMonth}`;
  }

  getInvoiceTypeTranslationKey(invoice: BillingInvoice) {
    if (invoice.invoice_type === 'reversal') {
      return 'Billing.Invoices.Reversal';
    }

    return `Dashboard.OpenParkingType.${invoice.product_category}`;
  }

  regenerateInvoice(invoice: BillingInvoice) {
    const regenerateUrl = invoice.regenerate_url;
    if (!regenerateUrl) {
      return;
    }

    this.billingApiService
      .regenerateInvoice(regenerateUrl)
      .pipe(showSpinner())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.clearRegenerateUrl(regenerateUrl);
          this.toastService.add({
            message: this.translateService.instant('Billing.Invoices.RegenerateInvoiceSuccess'),
            type: 'success',
          });
        },
        error: () => {
          this.toastService.add({
            message: this.translateService.instant('Common.Errors.UnknownError'),
            type: 'error',
          });
        },
      });
  }

  // an invoice and its reversals share the same regenerate url, correcting the invoice corrects all of them
  private clearRegenerateUrl(regenerateUrl: string) {
    Object.values(this.invoices).forEach((invoicePeriod) =>
      invoicePeriod.invoices.forEach((invoice) => {
        if (invoice.regenerate_url === regenerateUrl) {
          invoice.regenerate_url = null;
        }
      }),
    );
  }

  sendInvoiceDownloadedEventToAmplitude(invoice: BillingInvoice) {
    const invoiceAgeInDays = invoice.invoice_date
      ? Math.max(0, Math.floor((Date.now() - invoice.invoice_date.getTime()) / (1000 * 60 * 60 * 24)))
      : 0;

    this.amplitudeEventService.trackEvent(
      new InvoiceDownloaded({
        invoiceStatus: invoice.status,
        invoiceAgeInDays: invoiceAgeInDays,
        invoiceHasAdditionalFees: !!invoice.fees,
      }),
    );
  }
}
